import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import {Link, useHistory, useLocation, useParams} from "react-router-dom";
import {createBooking, updateBooking} from "../../utils/booking/api";
import {defaultBooking, _defaultBooking, useBookingPrice} from "../../utils/booking";
import {UserContext} from "../../App";
import FormDetails from "../../utils/booking/FormDetails";
import FormPassenger from "../../utils/booking/FormPassenger";
import FormPickup from "../../utils/booking/FormPickup";
import FormDropoff from "../../utils/booking/FormDropoff";
import FormDriver from "../../utils/booking/FormDriver";
import {db} from "../../index";
import {deleteBooking} from "../../utils/booking/api";
import FormTrack from "../../utils/booking/FormTrack";
import {useFolders} from "../../utils/folder";
import {FooterActionsContainer} from "../layout/Footer";
import {OutlineButton} from "../layout/OutlineButton";
import {NavItem, NavTabs} from "../layout/Nav";
import {stopLoading} from "../../utils/loader";
import {isExternal} from "../../utils/users";

export default function BookingAddOrUpdateContent() {
  const history = useHistory();
  const params = useParams();
  const {userInfos} = useContext(UserContext);

  const [booking, setBooking] = useState(defaultBooking());
  const bookingParamId = params?.booking_id || null;

  const [activeTab, setActiveTab] = useState("passenger");

  const [isLoaded, setIsLoaded] = useState(false);
  const [formError, setFormError] = useState([]);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [driverResetWarning, setDriverResetWarning] = useState(false);

  // Trick to refresh map display when navigating to its tab
  const [mapResize, setMapResize] = useState(0);

  const bookingName = booking.name;
  // const [flightDetails, setFlightDetails] = useState(null);
  const {folders: rawFolders} = useFolders();

  const folders = rawFolders.filter((folder) => {
    if (folder?.sharedBy) return false;
    if (folder?.invoice_status && folder?.invoice_status !== "draft") return false;
    if (folder?.estimate_status === "accepted" || folder?.estimate_status === "paid") {
      return false;
    }

    return true;
  });

  const location = useLocation();

  const urlFolderId = useMemo(() => {
    let searchParams = new URLSearchParams(location.search);
    return searchParams.get("folder_id");
  }, [location.search]);
  const bookingFolder = rawFolders.find((f) => f.id === booking.folder_id);

  // region helpers
  const setBookingField = useCallback((path, value) => {
    let fields = path.split(".");

    setBooking((old) => {
      let res = {...old};
      let tmp = res;
      for (let i = 0; i < fields.length; i++) {
        let field = fields[i];
        if (i === fields.length - 1) {
          tmp[field] = value;
        } else {
          tmp = res[field];
        }
      }

      return res;
    });
  }, []);

  const onDriverReset = useCallback(() => {
    setDriverResetWarning(true);
  }, []);

  function handleChange(e) {
    const targetName = e.target.name;
    const targetValue = e.target.value;

    // If vehicle changes and a driver was selected, reset the driver
    if (targetName === 'vehicle' && booking.driver?.id) {
      setBookingField('driver', _defaultBooking.driver);
      setDriverResetWarning(true);
    }

    // Clear warning when driver is manually changed
    if (targetName.startsWith('driver.')) {
      setDriverResetWarning(false);
    }

    setBookingField(targetName, targetValue);
  }

  // endregion

  // region set booking's provider if folder changed
  useEffect(() => {
    async function applyChange() {
      if (!isLoaded || !booking.folder_id || !rawFolders) return;

      let folder = rawFolders.find((f) => f.id === booking.folder_id);
      let provider_id = folder?.provider?.id;

      if (provider_id) {
        let providerSnap = await db
          .ref(`users/${userInfos.uid}/provider/${provider_id}`)
          .once("value");
        let provider = providerSnap.val();

        if (booking.provider?.id !== provider_id) {
          setBookingField("provider", provider);
        }
      }
    }

    applyChange().then();
  }, [booking.folder_id, booking.provider?.id, isLoaded, rawFolders, setBookingField, userInfos.uid,]);
  // endregion

  // region set folder and tarif from url's folder id
  useEffect(() => {
    if (!isLoaded || booking.id || booking.folder_id || booking.tarif || !urlFolderId || !folders) return;

    let urlFolder = folders.find((f) => f.id === urlFolderId);

    if (urlFolder && urlFolder?.tarif) {
      setBookingField("folder_id", urlFolderId);
      setBookingField("tarif", urlFolder?.tarif);
      setBookingField("tarif_product", urlFolder?.tarif_product);
    }
  }, [booking.folder_id, booking.id, urlFolderId, setBookingField, booking.tarif, folders, isLoaded,]);
  // endregion

  // region init booking (if update)
  useEffect(() => {
    async function getData() {
      if (bookingParamId) {
        let snap = await db
          .ref(`users/${userInfos.uid}/booking/${bookingParamId}`)
          .once("value");
        let data = snap.val() || {};
        setBooking({...defaultBooking(), ...data});
      }
      setIsLoaded(true);
    }

    getData().then();
  }, [bookingParamId, userInfos.uid]);
  // endregion

  // region prices
  const {
    prices, loading: loadingPrice, error: errorPrice,
  } = useBookingPrice(
    userInfos.jwt,
    booking.pickup?.geocoding_address || booking.pickup?.address,
    booking.dropoff?.geocoding_address || booking.dropoff?.address,
    booking.type,
    booking.num_hours,
    booking.vehicle,
    booking.tarif,
    booking.tarif_product,
    false, // requireTarif = false to allow distance/duration display without tariff
    booking.pickup?.place_id,
    booking.dropoff?.place_id,
    // Pass coordinates separately (not as objects to avoid infinite loop)
    booking.pickup?.lat,
    booking.pickup?.lon,
    booking.dropoff?.lat,
    booking.dropoff?.lon
  );

  useEffect(() => {
    if (prices && !errorPrice && !loadingPrice) {
      setBooking((old) => {
        if (old.sharedBy) return old;

        // For existing bookings: only update prices if they are valid (non-zero)
        // This prevents overwriting existing prices with 0 when no tarif is selected
        if (bookingParamId) {
          const hasValidPrices = prices.partner > 0 && prices.client > 0;
          return {
            ...old,
            ...(hasValidPrices && {
              driver_price: prices.partner,
              amount: prices.client,
            }),
            distance: prices.distance || old.distance,
            duration: prices.duration || old.duration,
          };
        }

        // For new bookings, always use calculated prices
        return {
          ...old,
          driver_price: prices.partner,
          amount: prices.client,
          distance: prices.distance,
          duration: prices.duration,
        };
      });
    }
  }, [
    bookingParamId, 
    errorPrice, 
    loadingPrice, 
    prices, 
    booking.tarif, 
    booking.vehicle, 
    booking.pickup?.geocoding_address, 
    booking.dropoff?.geocoding_address,
    // Add coordinates to dependencies to trigger update when predefined addresses change
    booking.pickup?.lat,
    booking.pickup?.lon,
    booking.dropoff?.lat,
    booking.dropoff?.lon
  ]);


  // endregion

  // region submissions
  async function handleFormSubmit(e) {
    e.preventDefault();
    if (handleValidation()) {
      setIsSubmiting(true);
      try {
        if (!bookingParamId) {
          await createBooking(userInfos.jwt, booking);
          history.push("/");
        } else {
          await updateBooking(userInfos.jwt, booking);
          window.alert("La course a été mise à jour avec succès");
        }
        setDriverResetWarning(false);
      } catch (e) {
        setFormError([e.response.data]);
        stopLoading();
      }
      setIsSubmiting(false);
    }
  }

  function handleValidation() {
    let valid = true;
    let message = [];

    if (booking.firstname === "") {
      valid = false;
      message.push("Veuillez renseigner un passager");
    }

    if (booking.passengers === "") {
      valid = false;
      message.push("Veuillez renseigner le nombre de passagers");
    }

    if (booking.date === "") {
      valid = false;
      message.push("Veuillez renseigner la date du départ");
    } else {
      // Validate that the date is valid
      const dateObj = new Date(booking.date * 1000);
      if (isNaN(dateObj.getTime()) || dateObj.getFullYear() < 1900 || dateObj.getFullYear() > 2100) {
        valid = false;
        message.push("Le format de la date est invalide. Veuillez saisir une date valide.");
      } else {
        // Additional validation: check if the date actually matches what was entered
        const enteredYear = dateObj.getFullYear();
        const enteredMonth = dateObj.getMonth();
        const enteredDay = dateObj.getDate();

        // Check if the parsed date matches the original timestamp
        const reconstructedDate = new Date(enteredYear, enteredMonth, enteredDay);
        const originalTimestamp = booking.date * 1000;

        if (Math.abs(reconstructedDate.getTime() - originalTimestamp) > 24 * 60 * 60 * 1000) {
          valid = false;
          message.push("Le format de la date est invalide. Veuillez saisir une date valide.");
        } else {
          // Check if date is in the past
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dateObj.setHours(0, 0, 0, 0);

          if (dateObj < today) {
            valid = false;
            message.push("La date de la course ne peut pas être dans le passé.");
          }
        }
      }
    }

    if (booking.time === "") {
      valid = false;
      message.push("Veuillez renseigner l'heure du départ");
    }

    if (booking.pickup.address === "") {
      valid = false;
      message.push("Veuillez renseigner l'adresse de départ");
    }

    if (booking.dropoff.address === "") {
      valid = false;
      message.push("Veuillez renseigner l'adresse d'arrivée");
    }

    if (booking.type === "") {
      valid = false;
      message.push("Veuillez renseigner le type de trajet");
    }

    if (booking.vehicle === "") {
      valid = false;
      message.push("Veuillez renseigner le type de véhicule");
    }

    if (booking.type === "hourly" && (!booking.num_hours || booking.num_hours <= 0)) {
      valid = false;
      message.push("Veuillez renseigner une durée valide (supérieure à 0) pour un trajet à l'heure");
    }

    // if (booking.status === "") {
    //   valid = false;
    //   message.push("Veuillez renseigner le statut de paiement");
    // }

    // if (!booking.driver_price) {
    //   valid = false;
    //   message.push("Veuillez renseigner le prix du chauffeur");
    // }

    setFormError(message);
    return valid;
  }

  async function handleDeleteBooking(e) {
    e.preventDefault();
    try {
      await deleteBooking(userInfos.jwt, booking.id);
    } catch (e) {
      window.alert("Erreur lors de la suppression : " + e.response.data);
      stopLoading();
      return;
    }
    history.push("/");
  }

  // endregion

  // region Alerts/Button JSX
  const FormAlerts = useMemo(() => {
    return formError.length ? (<div
      className="alert alert-danger mt-3"
      role="alert"
      style={{flexDirection: "column", borderRadius: "0"}}
    >
      {formError.map((error, index) => {
        return (<div
          className="alert-text"
          key={index}
          style={{alignSelf: "flex-start"}}
        >
          {error}
        </div>);
      })}
    </div>) : null;
  }, [formError]);

  const SubmitButton = isSubmiting ? (<button type="submit" className="btn btn-primary px-5" disabled>
    {bookingParamId ? "Mise à jour..." : "Création..."}
  </button>) : (<button type="submit" className="btn btn-primary px-5">
    {bookingParamId ? "Mettre à jour la course" : "Créer la course"}
  </button>);

  // endregion

  return (<div
    className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
    id="kt_content"
  >
    <div className="kt-subheader   kt-grid__item" id="kt_subheader">
      <div className="kt-container  kt-container--fluid ">
        <div className="kt-subheader__main">
          <h3 className="kt-subheader__title">
            {bookingParamId ? bookingName : `Créer une course${bookingFolder?.name ? ' dans le dossier "' + bookingFolder.name + '"' : ""}`}{" "}
          </h3>
          <div className="kt-subheader__breadcrumbs">
            <Link to="/" className="kt-subheader__breadcrumbs-home">
              <i className="flaticon2-shelter"></i>
            </Link>
            <span className="kt-subheader__breadcrumbs-separator"></span>
            <Link to="/booking" className="kt-subheader__breadcrumbs-link">
              Courses
            </Link>
            {bookingParamId ? (<>
              <span className="kt-subheader__breadcrumbs-separator"></span>
              <Link
                to={"/booking/" + booking.id}
                className="kt-subheader__breadcrumbs-link"
              >
                {bookingName}
              </Link>
            </>) : null}
          </div>
        </div>
      </div>
    </div>

    <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
      <div className="kt-portlet kt-portlet--tabs">
        <div className="kt-portlet__head">
          <div className="kt-portlet__head-label">
            <h3 className="kt-portlet__head-title">{booking.id}</h3>
          </div>
          <div className="d-flex align-items-center">
            <NavTabs style={{margin: 0}}>
              <NavItem className={activeTab === "passenger" ? "active" : ""}>
                <button
                  data-toggle="tab"
                  role="tab"
                  onClick={() => setActiveTab("passenger")}
                >
                  Passager
                </button>
              </NavItem>
              {!isExternal(userInfos) &&
                <>
                  <NavItem className={activeTab === "driver" ? "active" : ""}>
                    <button
                      data-toggle="tab"
                      role="tab"
                      onClick={() => setActiveTab("driver")}
                    >
                      Chauffeur
                    </button>
                  </NavItem>
                  <NavItem className={activeTab === "details" ? "active" : ""}>
                    <button
                      data-toggle="tab"
                      role="tab"
                      onClick={() => setActiveTab("details")}
                    >
                      Détails
                    </button>
                  </NavItem>
                </>}
              {bookingParamId ? (<NavItem className={activeTab === "track" ? "active" : ""}>
                <button
                  data-toggle="tab"
                  role="tab"
                  onClick={() => {
                    setActiveTab("track");
                    setMapResize((old) => old + 1);
                  }}
                >
                  Suivi
                </button>
              </NavItem>) : null}
            </NavTabs>
          </div>
        </div>
        <form onSubmit={handleFormSubmit} className="kt-form">
          <div className="kt-portlet__body">
            <div className="tab-content">
              <div
                className={"tab-pane" + (activeTab === "passenger" ? " active" : "")}
                id="passenger"
              >
                <FormPassenger
                  booking={booking}
                  folder={bookingFolder}
                  setBookingField={setBookingField}
                  handleChange={handleChange}
                  userInfos={userInfos}
                  isLoaded={isLoaded}
                />
                <hr/>
                <h4>Départ</h4>
                <FormPickup
                  booking={booking}
                  setBookingField={setBookingField}
                  handleChange={handleChange}
                  isLoaded={isLoaded}
                  isNewBooking={!bookingParamId}
                  onDriverReset={onDriverReset}
                />
                <hr/>
                <h4>Arrivée</h4>
                <FormDropoff
                  booking={booking}
                  setBookingField={setBookingField}
                  isLoaded={isLoaded}
                />
                <hr/>
                <h4>Informations</h4>
                <div className="kt-section kt-section--first">
                  <div className="kt-section__body">
                    <div className="form-group row">
                      <label className="col-lg-3 col-form-label">
                        Informations générales:
                      </label>
                      <div className="col-lg-6">
                        <textarea
                          value={booking.additional_info || ""}
                          className="form-control"
                          name="additional_info"
                          onChange={handleChange}
                          disabled={booking.sharedBy}
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {!isExternal(userInfos) &&
                <>
                  <div
                    className={"tab-pane" + (activeTab === "driver" ? " active" : "")}
                    id="driver"
                  >
                    <FormDriver
                      userInfos={userInfos}
                      booking={booking}
                      setBooking={setBooking}
                      setBookingField={setBookingField}
                      isLoaded={isLoaded}
                    />
                  </div>
                  <div
                    className={"tab-pane" + (activeTab === "details" ? " active" : "")}
                    id="details"
                  >
                    <FormDetails
                      booking={booking}
                      selectedFolder={bookingFolder}
                      setBookingField={setBookingField}
                      handleChange={handleChange}
                      userInfos={userInfos}
                      folders={folders}
                      calculatedPrice={prices}
                      errorPrice={errorPrice}
                      loadingPrice={loadingPrice}
                      isLoaded={isLoaded}
                    />
                  </div>
                </>}
              {bookingParamId ? (<div
                className={"tab-pane" + (activeTab === "track" ? " active" : "")}
                id="track"
              >
                <FormTrack
                  booking={booking}
                  isLoaded={isLoaded}
                  mapResize={mapResize}
                />
              </div>) : null}
            </div>
          </div>
          <FooterActionsContainer>
            <div>
              {!booking?.sharedBy && (
                <>
                  <div className="form-check form-check-inline">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={booking.hide_phone}
                      name="hide_phone"
                      onChange={(e) => setBookingField("hide_phone", e.target.checked)}
                      disabled={booking.sharedBy}
                    />
                    <label className="form-check-label">
                      Cacher téléphone pour le chauffeur
                    </label>
                  </div>
                  <label className="d-flex align-items-center form-check form-check-inline">
                    <input
                      type="checkbox"
                      name="dontSendUpdateMailToCustomer"
                      className="mr-2 form-check-input"
                      checked={booking.dontSendUpdateMailToCustomer}
                      onChange={(e) => setBookingField("dontSendUpdateMailToCustomer", e.target.checked)}
                      disabled={booking.sharedBy}
                    />
                    <span>
                      Ne pas envoyer de mail de mise à jour au client.
                    </span>
                  </label>
                  <label className="d-flex align-items-center form-check form-check-inline">
                    <input
                      type="checkbox"
                      name="dontSendWhatsappToCustomer"
                      className="mr-2 form-check-input"
                      checked={booking.dontSendWhatsappToCustomer}
                      onChange={(e) => setBookingField("dontSendWhatsappToCustomer", e.target.checked)}
                      disabled={booking.sharedBy}
                    />
                    <span>
                      Ne pas envoyer de notifications WhatsApp au client.
                    </span>
                  </label>
                  <label className="d-flex align-items-center form-check form-check-inline">
                    <input
                      type="checkbox"
                      name="disableAllNotifications"
                      className="mr-2 form-check-input"
                      checked={booking.disableAllNotifications}
                      onChange={(e) => setBookingField("disableAllNotifications", e.target.checked)}
                      disabled={booking.sharedBy}
                    />
                    <span>
                      Désactiver l'ensemble des notifications.
                    </span>
                  </label>
                </>
              )}
              {driverResetWarning && (
                <div className="alert alert-warning mt-3" style={{backgroundColor: '#fff3cd', borderColor: '#ffecb5', color: '#856404'}}>
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  Vous avez modifié un ou plusieurs champs impactant le choix du chauffeur, merci de vous assurer que votre choix de chauffeur est correct avant de sauvegarder cette course.
                </div>
              )}
            </div>
            <div></div>
            <div></div>
            <div>
              <OutlineButton
                onClick={(e) => {
                  e.preventDefault();
                  history.goBack();
                }}
              >
                <i className="fa fa-arrow-left"></i>
                Retour
              </OutlineButton>
            </div>
            <div>{SubmitButton}</div>
            <div>
              {bookingParamId && !booking?.sharedBy ? (
                <OutlineButton
                  type="button"
                  data-toggle="modal"
                  data-target="#delete_modal"
                >
                  <i className="fa fa-trash"></i>
                  Supprimer
                </OutlineButton>
              ) : null}
            </div>          </FooterActionsContainer>
        </form>
        {FormAlerts}
      </div>
    </div>
    <div
      className="modal fade"
      id="delete_modal"
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Supprimer la course</h5>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <p>Êtes-vous sûr de vouloir supprimer cette course ?</p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-dismiss="modal"
            >
              Annuler
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-dismiss="modal"
              onClick={handleDeleteBooking}
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>)
    ;
}
